/** @jsxImportSource @emotion/react */
import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as snackbarActions from "../../../../actions/snackbarActions";
import PageTitle from "../../../Utils/smsTitle";
import { SubHeader } from "../../../../Containers/SubHeader";
import MainSnackbar from "../../../Utils/Snackbar/SmsSnackbar";
import ObGynSidebar from "../../Utils/ObGynSidebar";
import ObGynPatientInfo from "../../Utils/ObGynPatientInfo";
import { useEMTCT } from "./hooks/useEMTCT";
import { EMTCTAssessmentForm } from "./EMTCTAssessmentForm";
import { EMTCTTestingAlgorithm } from "./EMTCTTestingAlgorithm";
import { EMTCTHistoryTimeline } from "./EMTCTHistoryTimeline";
import { history } from "../../../../Helpers/history";
import {
    EMTCTPageWrapper,
    EMTCTHeader,
    EMTCTHeaderTitle,
    EMTCTHeaderStats,
    StatBadge,
    AlertBanner,
    EmptyState,
    GoToBookingBtn,
    LoadingWrapper,

} from "./EMTCTStyles";
import { ObGynTabBar, ObGynTabBtn, ObGynTabBadge } from "../../Utils/ObGynTabStyles";

/**
 * EMTCTPage — Page-level container for eMTCT (HIV in Pregnancy) module
 *
 * Route: /gyn-and-obs-emtct/:patient_number/:visit_id/:patient_type
 */
const EMTCTPage = function(props) {
    const actions = props.actions;
    const snackbar = props.snackbar;
    const params = props.match.params;
    const patient_number = params.patient_number;
    const visit_id = params.visit_id;
    const patient_type = params.patient_type;

    /* ─── Current user ─── */
    const user = JSON.parse(sessionStorage.getItem("user"));
    const userObj = !user ? {} : user;
    const token = !userObj.token ? [] : userObj.token;
    const _token = !token[1] ? {} : token[1];
    const doctor_name = (!_token.first_name ? "" : _token.first_name) + " " +
        (!_token.last_name ? "" : _token.last_name);

    /* ─── eMTCT hook ─── */
    const em = useEMTCT(actions, patient_number, visit_id);

    /* ─── Snackbar ─── */
    const closeSnackbar = function() { actions.snackbarActions.hideSnackbar(); };

    /* ─── Navigate to ANC booking ─── */
    const goToBooking = function() {
        history.push(
            "/gyn-and-obs-antenatal-card/" + patient_number + "/" + visit_id + "/" + patient_type
        );
    };


    return (
        <div className="journals">
            <PageTitle title="eMTCT — HIV in Pregnancy" />
            <SubHeader
                title="OB/GYN"
                subTitle="eMTCT — HIV in Pregnancy"
                doctor_name={doctor_name}
            >
                <div className="header-icon-margin-right-zero">
                    <img
                        src="/images/antenatal-blue.png"
                        className="icon-resize surgery-icon-tabs ante-natal-green"
                        alt="ANC"
                    />
                </div>
            </SubHeader>
            <MainSnackbar
                variant={snackbar.type}
                open={snackbar.openBar}
                message={snackbar.message}
                handleCloseBar={closeSnackbar}
            />

            <ObGynSidebar
                patient_number={patient_number}
                visit_id={visit_id}
                patient_type={patient_type}
            >
                <EMTCTPageWrapper>
                    {/* ═══ PATIENT INFO ═══ */}
                    <ObGynPatientInfo
                        patient_number={patient_number}
                        visit_id={visit_id}
                    />

                    {/* ═══ LOADING STATE ═══ */}
                    {em.loadingBooking && (
                        <LoadingWrapper>
                            <span></span> Loading patient ANC data...
                        </LoadingWrapper>
                    )}

                    {/* ═══ NO BOOKING STATE ═══ */}
                    {!em.loadingBooking && !em.bookingData && (
                        <div style={{ marginTop: "16px" }}>
                            <EmptyState>
                                <div className="empty-icon">🤰</div>
                                <div className="empty-title">No Active ANC Booking Found</div>
                                <div className="empty-desc">
                                    This patient must be registered in the Antenatal Care program
                                    before eMTCT assessment can be documented. 
                                </div>
                                <GoToBookingBtn onClick={goToBooking}>
                                    Go to ANC Registration & Booking
                                </GoToBookingBtn>
                            </EmptyState>
                        </div>
                    )}

                    {/* ═══ MAIN CONTENT ═══ */}
                    {!em.loadingBooking && em.bookingData && (
                        <div style={{ marginTop: "16px" }}>

                            {/* ─── View Toggle ─── */}
                            <ObGynTabBar>
                                <ObGynTabBtn
                                    active={em.activeView === "form"}
                                    onClick={function() { em.toggleView("form"); }}
                                >
                                    Record
                                </ObGynTabBtn>
                                <ObGynTabBtn
                                    active={em.activeView === "history"}
                                    onClick={function() { em.toggleView("history"); }}
                                >
                                    History
                                    {em.emtctHistory.length > 0 && (
                                        <ObGynTabBadge active={em.activeView === "history"}>{em.emtctHistory.length}</ObGynTabBadge>
                                    )}
                                </ObGynTabBtn>
                            </ObGynTabBar>

                            {/* ─── Form ─── */}
                            {em.activeView === "form" && (
                                <EMTCTAssessmentForm
                                    formData={em.formData}
                                    handleChange={em.handleChange}
                                    toggleBool={em.toggleBool}
                                    isHivPositive={em.isHivPositive}
                                    activeStep={em.activeStep}
                                    handleNext={em.handleNext}
                                    handleBack={em.handleBack}
                                    handleStepClick={em.handleStepClick}
                                    saveEmtct={em.saveEmtct}
                                    saving={em.saving}
                                    constants={{
                                        WHO_STAGES:             em.WHO_STAGES,
                                        ART_REGIMENS:           em.ART_REGIMENS,
                                        FEEDING_OPTIONS:        em.FEEDING_OPTIONS,
                                        PARTNER_RESULT_OPTIONS: em.PARTNER_RESULT_OPTIONS,
                                    }}
                                />
                            )}

                            {/* ─── History ─── */}
                            {em.activeView === "history" && (
                                <EMTCTHistoryTimeline
                                    records={em.emtctHistory}
                                    loading={em.loadingHistory}
                                />
                            )}
                        </div>
                    )}
                </EMTCTPageWrapper>
            </ObGynSidebar>
        </div>
    );
};

function mapStateToProps(state) {
    return { snackbar: state.snackbar };
}

function mapDispatchToProps(dispatch) {
    return {
        actions: {
            snackbarActions: bindActionCreators(snackbarActions, dispatch),
        },
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(EMTCTPage);
